import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useDialog } from "@/context/DialogContext";
import {
  ArrowLeft, User as UserIcon, Envelope, Phone, FileText, Paperclip, Star,
  CalendarBlank, Video, MapPin, Link as LinkIcon, Users, X, CheckCircle,
  PencilSimple, Trash, ThumbsUp, ThumbsDown, Minus, Clock, ChatCircle,
} from "@phosphor-icons/react";

const STAGES = ["applied", "screening", "interview", "offer", "hired", "rejected", "withdrawn"];
const STAGE_COLORS = {
  applied: "bg-blue-100 text-blue-800 border-blue-300",
  screening: "bg-amber-100 text-amber-800 border-amber-300",
  interview: "bg-purple-100 text-purple-800 border-purple-300",
  offer: "bg-emerald-100 text-emerald-800 border-emerald-300",
  hired: "bg-green-100 text-green-800 border-green-400",
  rejected: "bg-red-100 text-red-800 border-red-300",
  withdrawn: "bg-gray-100 text-gray-700 border-gray-300",
};

export default function ApplicationDetail() {
  const { aid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { confirm } = useDialog();
  const [app, setApp] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [ivOpen, setIvOpen] = useState(false);
  const [rvOpen, setRvOpen] = useState(false);
  const [ivEdit, setIvEdit] = useState(null);

  const canManage = can("manage_applications");
  const canSchedule = can("schedule_interviews");
  const canReview = can("review_applications");
  const isPanelist = useMemo(() => {
    if (!app) return false;
    return (app.interviews || []).some(iv => (iv.panel_ids || []).includes(user?.id));
  }, [app, user]);
  const isCurrentReviewer = app?.current_reviewer_id === user?.id;

  const flash = (t, x) => { setMsg({ type: t, text: x }); setTimeout(() => setMsg({ type: "", text: "" }), 3500); };

  const load = async () => {
    setLoading(true);
    try {
      const [ar, ur] = await Promise.all([
        api.get(`/applications/${aid}`),
        api.get("/users"),
      ]);
      setApp(ar.data);
      setUsers(ur.data || []);
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [aid]);

  const setStatus = async (newStatus) => {
    try {
      await api.patch(`/applications/${aid}`, { status: newStatus });
      await load();
      flash("ok", `Status → ${newStatus}`);
    } catch (e) { flash("error", formatAxiosError(e)); }
  };
  const setReviewer = async (uid) => {
    try {
      await api.patch(`/applications/${aid}`, { current_reviewer_id: uid });
      await load();
      flash("ok", "Reviewer updated");
    } catch (e) { flash("error", formatAxiosError(e)); }
  };
  const deleteInterview = async (iid) => {
    const ok = await confirm({
      title: "Delete this interview?",
      description: "The interview and any panel feedback attached to it will be removed.",
      confirmText: "Delete interview",
      variant: "destructive",
    });
    if (!ok) return;
    try { await api.delete(`/interviews/${iid}`); await load(); flash("ok", "Interview deleted"); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };
  const deleteReview = async (rid) => {
    const ok = await confirm({
      title: "Delete this review?",
      description: "The reviewer's scores and comments will be removed.",
      confirmText: "Delete review",
      variant: "destructive",
    });
    if (!ok) return;
    try { await api.delete(`/reviews/${rid}`); await load(); flash("ok", "Review deleted"); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!app) return <div className="p-8">Application not found.</div>;

  const canSubmitReview = canReview || isPanelist || isCurrentReviewer;

  return (
    <div className="p-8 lg:p-12">
      <button onClick={() => nav(`/jobs/${app.job_id}/applicants`)} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
        <ArrowLeft size={14} weight="bold" /> Back to pipeline
      </button>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`}>{msg.text}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Candidate info */}
        <div className="lg:col-span-1 space-y-4">
          <div className="border border-border p-5 bg-white">
            <div className="eyebrow mb-2">Candidate</div>
            <h1 className="font-display text-2xl font-black leading-tight">{app.candidate_name}</h1>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex items-center gap-2"><Envelope size={12} weight="bold" className="text-muted-foreground" /> <a href={`mailto:${app.candidate_email}`} className="hover:underline">{app.candidate_email}</a></div>
              {app.candidate_phone && <div className="flex items-center gap-2"><Phone size={12} weight="bold" className="text-muted-foreground" /> {app.candidate_phone}</div>}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase border ${STAGE_COLORS[app.status]}`}>{app.status}</span>
              <span className="text-[10px] px-2 py-0.5 border border-border bg-muted">via {app.source}</span>
            </div>
            {app.job && (
              <Link to={`/jobs/${app.job.id}`} className="mt-3 block text-xs text-[#002FA7] hover:underline">
                For: {app.job.title}
              </Link>
            )}
            {app.referrer && (
              <div className="mt-3 border border-purple-200 bg-purple-50 px-3 py-2 text-xs">
                <div className="font-semibold text-purple-900">Referred by</div>
                <div>{app.referrer.name} — {app.referrer.email}</div>
              </div>
            )}
          </div>

          {/* Attachments */}
          <div className="border border-border p-5 bg-white">
            <div className="eyebrow mb-2">Documents</div>
            {app.resume_file ? (
              <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${app.resume_file.file_id}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 border border-border px-3 py-2 hover:bg-muted text-sm">
                <FileText size={14} weight="bold" className="text-[#002FA7]" />
                <span className="flex-1 truncate">{app.resume_file.filename || "Resume"}</span>
                <span className="text-xs text-muted-foreground">Resume</span>
              </a>
            ) : <div className="text-xs text-muted-foreground italic">No resume uploaded</div>}
            {(app.attachment_files || []).length > 0 && (
              <ul className="mt-2 space-y-1">
                {app.attachment_files.map(m => (
                  <li key={m.file_id}>
                    <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${m.file_id}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 border border-border px-3 py-1.5 hover:bg-muted text-xs">
                      <Paperclip size={12} weight="bold" />
                      <span className="flex-1 truncate">{m.filename}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Actions panel */}
          {canManage && (
            <div className="border border-border p-5 bg-white">
              <div className="eyebrow mb-2">Actions</div>
              <label className="block mb-3">
                <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-1">Move to stage</span>
                <select className="input" value={app.status} onChange={(e) => setStatus(e.target.value)} data-testid="stage-select">
                  {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-1">Assign next reviewer</span>
                <select className="input" value={app.current_reviewer_id || ""} onChange={(e) => setReviewer(e.target.value)} data-testid="reviewer-select">
                  <option value="">— None —</option>
                  {users.filter(u => !u.disabled).map(u => (
                    <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
                  ))}
                </select>
                {app.current_reviewer && (
                  <div className="text-xs text-muted-foreground mt-1">Current: <b>{app.current_reviewer.name}</b></div>
                )}
              </label>
            </div>
          )}

          {/* Timeline */}
          <div className="border border-border p-5 bg-white">
            <div className="eyebrow mb-2">Stage history</div>
            <ol className="space-y-2 text-xs">
              {(app.stage_history || []).slice().reverse().map((h, i) => {
                const by = users.find(u => u.id === h.by);
                return (
                  <li key={i} className="flex items-start gap-2">
                    <div className={`w-2 h-2 mt-1.5 rounded-full ${STAGE_COLORS[h.status] || "bg-gray-300"}`} />
                    <div className="flex-1">
                      <div className="font-semibold">{h.status}</div>
                      <div className="text-muted-foreground">
                        {new Date(h.at).toLocaleString()} {by && `· ${by.name}`}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        {/* RIGHT: Interviews + Reviews */}
        <div className="lg:col-span-2 space-y-6">
          {/* Interviews */}
          <div className="border border-border bg-white">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-display text-lg font-black">Interviews</h2>
              {canSchedule && (
                <button onClick={() => { setIvEdit(null); setIvOpen(true); }} className="btn-primary flex items-center gap-2 text-xs" data-testid="schedule-interview-btn">
                  <CalendarBlank size={12} weight="bold" /> Schedule
                </button>
              )}
            </div>
            <div className="p-5">
              {(app.interviews || []).length === 0 ? (
                <div className="text-sm text-muted-foreground italic">No interviews scheduled yet.</div>
              ) : (
                <ul className="space-y-3">
                  {app.interviews.map(iv => (
                    <li key={iv.id} className="border border-border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 text-sm font-semibold">
                            <CalendarBlank size={14} weight="bold" className="text-[#002FA7]" />
                            {new Date(iv.scheduled_at).toLocaleString()} · {iv.duration_minutes}m
                          </div>
                          <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              {iv.mode === "video" ? <Video size={12} weight="bold" /> : iv.mode === "in_person" ? <MapPin size={12} weight="bold" /> : <Phone size={12} weight="bold" />}
                              {iv.mode.replace("_", " ")}
                            </span>
                            {iv.location && <span>{iv.location}</span>}
                            {iv.meeting_link && (
                              <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline flex items-center gap-1">
                                <LinkIcon size={10} weight="bold" /> Join
                              </a>
                            )}
                            <span className={`px-1.5 py-0 border text-[9px] font-semibold uppercase ${
                              iv.status === "completed" ? "bg-green-100 text-green-800 border-green-400"
                              : iv.status === "cancelled" ? "bg-red-100 text-red-800 border-red-300"
                              : iv.status === "no_show" ? "bg-orange-100 text-orange-800 border-orange-300"
                              : "bg-blue-100 text-blue-800 border-blue-300"
                            }`}>{iv.status}</span>
                          </div>
                          {(iv.panel || []).length > 0 && (
                            <div className="mt-2 flex items-center gap-1 text-xs flex-wrap">
                              <Users size={12} weight="bold" className="text-muted-foreground" />
                              {iv.panel.map(p => (
                                <span key={p.id} className="px-1.5 py-0.5 bg-muted border border-border">{p.name}</span>
                              ))}
                            </div>
                          )}
                          {iv.notes && <div className="mt-2 text-xs text-muted-foreground">{iv.notes}</div>}
                        </div>
                        {canSchedule && (
                          <div className="flex gap-1">
                            <button onClick={() => { setIvEdit(iv); setIvOpen(true); }} className="p-1 hover:bg-muted" title="Edit"><PencilSimple size={14} weight="bold" /></button>
                            <button onClick={() => deleteInterview(iv.id)} className="p-1 hover:bg-muted text-red-600" title="Delete"><Trash size={14} weight="bold" /></button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Reviews */}
          <div className="border border-border bg-white">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-display text-lg font-black">Feedback & Reviews</h2>
              {canSubmitReview && (
                <button onClick={() => setRvOpen(true)} className="btn-primary flex items-center gap-2 text-xs" data-testid="add-review-btn">
                  <Star size={12} weight="bold" /> Add review
                </button>
              )}
            </div>
            <div className="p-5">
              {(app.reviews || []).length === 0 ? (
                <div className="text-sm text-muted-foreground italic">No reviews yet. {canSubmitReview && "Be the first to weigh in."}</div>
              ) : (
                <ul className="space-y-4">
                  {app.reviews.map(rv => (
                    <li key={rv.id} className="border-l-4 border-[#002FA7] pl-4 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-sm font-bold">
                            {(rv.reviewer?.name || "?").charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-semibold">{rv.reviewer?.name || "Unknown"}</div>
                            <div className="text-xs text-muted-foreground">{new Date(rv.created_at).toLocaleString()}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star key={n} size={14} weight={n <= rv.rating ? "fill" : "regular"} className={n <= rv.rating ? "text-amber-500" : "text-gray-300"} />
                            ))}
                          </div>
                          <DecisionBadge decision={rv.decision} />
                          {(rv.reviewer_id === user?.id || canManage) && (
                            <button onClick={() => deleteReview(rv.id)} className="p-1 hover:bg-muted text-red-600" title="Delete"><Trash size={12} weight="bold" /></button>
                          )}
                        </div>
                      </div>
                      {rv.strengths && <div className="mt-2 text-sm"><b className="text-green-700">Strengths:</b> {rv.strengths}</div>}
                      {rv.concerns && <div className="mt-1 text-sm"><b className="text-red-700">Concerns:</b> {rv.concerns}</div>}
                      {rv.comments && <div className="mt-1 text-sm text-muted-foreground">{rv.comments}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {ivOpen && (
        <InterviewModal
          aid={aid}
          users={users}
          interview={ivEdit}
          onClose={() => { setIvOpen(false); setIvEdit(null); }}
          onSaved={async () => { setIvOpen(false); setIvEdit(null); await load(); flash("ok", "Interview saved."); }}
        />
      )}
      {rvOpen && (
        <ReviewModal
          aid={aid}
          interviews={app.interviews || []}
          onClose={() => setRvOpen(false)}
          onSaved={async () => { setRvOpen(false); await load(); flash("ok", "Review saved."); }}
        />
      )}
    </div>
  );
}

function DecisionBadge({ decision }) {
  if (decision === "hire") return <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase border bg-green-100 text-green-800 border-green-400 flex items-center gap-1"><ThumbsUp size={10} weight="bold" /> Hire</span>;
  if (decision === "no_hire") return <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase border bg-red-100 text-red-800 border-red-300 flex items-center gap-1"><ThumbsDown size={10} weight="bold" /> No hire</span>;
  return <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase border bg-amber-100 text-amber-800 border-amber-300 flex items-center gap-1"><Minus size={10} weight="bold" /> Maybe</span>;
}

function InterviewModal({ aid, users, interview, onClose, onSaved }) {
  const editing = !!interview;
  const [form, setForm] = useState({
    scheduled_at: interview?.scheduled_at?.slice(0, 16) || new Date().toISOString().slice(0, 16),
    duration_minutes: interview?.duration_minutes || 45,
    mode: interview?.mode || "video",
    location: interview?.location || "",
    meeting_link: interview?.meeting_link || "",
    panel_ids: interview?.panel_ids || [],
    status: interview?.status || "scheduled",
    notes: interview?.notes || "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const togglePanel = (uid) => {
    setForm(f => ({ ...f, panel_ids: f.panel_ids.includes(uid) ? f.panel_ids.filter(x => x !== uid) : [...f.panel_ids, uid] }));
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 30,
        mode: form.mode,
        location: form.location.trim(),
        meeting_link: form.meeting_link.trim(),
        panel_ids: form.panel_ids,
        notes: form.notes.trim(),
      };
      if (editing) {
        payload.status = form.status;
        await api.patch(`/interviews/${interview.id}`, payload);
      } else {
        await api.post(`/applications/${aid}/interviews`, payload);
      }
      onSaved();
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-xl font-black">{editing ? "Edit interview" : "Schedule interview"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-3">
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date & time*"><input className="input" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="iv-when" /></Field>
            <Field label="Duration (minutes)"><input className="input" type="number" min="1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></Field>
            <Field label="Mode">
              <select className="input" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="iv-mode">
                <option value="video">Video</option>
                <option value="in_person">In person</option>
                <option value="phone">Phone</option>
              </select>
            </Field>
            {editing && (
              <Field label="Status">
                <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="no_show">No show</option>
                </select>
              </Field>
            )}
            <Field label="Location (for in-person)"><input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
            <Field label="Meeting link (for video)"><input className="input" value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} placeholder="https://…" /></Field>
          </div>
          <Field label="Panel members">
            <div className="max-h-48 overflow-y-auto border border-border p-2 grid grid-cols-2 gap-1">
              {users.filter(u => !u.disabled).map(u => (
                <label key={u.id} className="flex items-center gap-2 text-xs px-2 py-1 hover:bg-muted cursor-pointer">
                  <input type="checkbox" checked={form.panel_ids.includes(u.id)} onChange={() => togglePanel(u.id)} />
                  <span className="flex-1 truncate">{u.name}</span>
                  <span className="text-muted-foreground">{u.role}</span>
                </label>
              ))}
            </div>
          </Field>
          <Field label="Notes"><textarea className="input min-h-[70px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-iv">{busy ? "Saving…" : "Save interview"}</button>
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ aid, interviews, onClose, onSaved }) {
  const [form, setForm] = useState({
    interview_id: "",
    rating: 3,
    decision: "maybe",
    strengths: "",
    concerns: "",
    comments: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/applications/${aid}/reviews`, {
        interview_id: form.interview_id || "",
        rating: Number(form.rating),
        decision: form.decision,
        strengths: form.strengths.trim(),
        concerns: form.concerns.trim(),
        comments: form.comments.trim(),
      });
      onSaved();
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-xl font-black">Submit review</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-3">
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          {interviews.length > 0 && (
            <Field label="Related interview (optional)">
              <select className="input" value={form.interview_id} onChange={(e) => setForm({ ...form, interview_id: e.target.value })}>
                <option value="">— None —</option>
                {interviews.map(iv => (
                  <option key={iv.id} value={iv.id}>
                    {new Date(iv.scheduled_at).toLocaleString()} · {iv.mode}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Rating">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setForm({ ...form, rating: n })} className="p-1" data-testid={`star-${n}`}>
                  <Star size={22} weight={n <= form.rating ? "fill" : "regular"} className={n <= form.rating ? "text-amber-500" : "text-gray-300"} />
                </button>
              ))}
            </div>
          </Field>
          <Field label="Decision">
            <div className="flex gap-2">
              {[
                { key: "hire", label: "Hire", color: "border-green-500 text-green-700 bg-green-50" },
                { key: "maybe", label: "Maybe", color: "border-amber-500 text-amber-700 bg-amber-50" },
                { key: "no_hire", label: "No hire", color: "border-red-500 text-red-700 bg-red-50" },
              ].map(d => (
                <button key={d.key} onClick={() => setForm({ ...form, decision: d.key })}
                  className={`px-3 py-1.5 text-xs font-semibold border ${form.decision === d.key ? d.color : "border-border bg-white"}`}
                  data-testid={`decision-${d.key}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Strengths"><textarea className="input min-h-[70px]" value={form.strengths} onChange={(e) => setForm({ ...form, strengths: e.target.value })} placeholder="What went well" /></Field>
          <Field label="Concerns"><textarea className="input min-h-[70px]" value={form.concerns} onChange={(e) => setForm({ ...form, concerns: e.target.value })} placeholder="Any red flags or areas of doubt" /></Field>
          <Field label="Comments"><textarea className="input min-h-[70px]" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></Field>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-rv">{busy ? "Saving…" : "Submit review"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="block">
      <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">{label}</span>
      {children}
    </div>
  );
}
